import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Plus, Save, Trash2, GripVertical, Eye, EyeOff, Shield, AlertTriangle,
  Users, User, Bot, X, Star, ChevronDown, Check, Info, Play, Wallet, Mail, Bell,
  MessageSquare, Calendar, FileText,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import WorkflowTimeline from "@/components/dossier/WorkflowTimeline";
import ClientTimeline from "@/components/client/ClientTimeline";

const ROLE_OPTS = [
  { key: "agency",  label: "Agence" },
  { key: "seller",  label: "Commercial" },
  { key: "client",  label: "Client" },
  { key: "system",  label: "Système" },
];

const uid = () => Math.random().toString(36).slice(2, 10);

// ------- Small primitives ---------------------------------------------
const Toggle = ({ checked, onChange, testId, disabled }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={[
      "relative h-6 w-10 rounded-full transition-colors shrink-0",
      disabled ? "opacity-50 cursor-not-allowed" : "",
      checked ? "bg-[#111111]" : "bg-[#E5E7EB]",
    ].join(" ")}
  >
    <span className={[
      "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-all",
      checked ? "left-[18px]" : "left-0.5",
    ].join(" ")} />
  </button>
);

const Row = ({ label, hint, children, testId }) => (
  <div className="flex items-start justify-between gap-4 py-3" data-testid={testId}>
    <div className="min-w-0">
      <p className="text-[13px] font-medium text-[#111111]">{label}</p>
      {hint && <p className="mt-0.5 text-[11.5px] text-[#6B7280] leading-relaxed">{hint}</p>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="mt-6">
    <p className="text-[10.5px] font-semibold text-[#6B7280] uppercase tracking-wider">{title}</p>
    <div className="mt-2 divide-y divide-[#EEF2F5]">{children}</div>
  </section>
);

// ------- Step Card in list ---------------------------------------------
const StepCard = ({ step, index, onOpen, onDragStart, onDragOver, onDrop, dragging, canEdit }) => (
  <div
    data-testid={`step-card-${step.key || step.id}`}
    draggable={canEdit}
    onDragStart={canEdit ? (e) => onDragStart(e, index) : undefined}
    onDragOver={canEdit ? onDragOver : undefined}
    onDrop={canEdit ? (e) => onDrop(e, index) : undefined}
    onClick={() => onOpen(step)}
    className={[
      "flex items-center gap-3 rounded-2xl border bg-white p-4 cursor-pointer transition-all",
      dragging ? "opacity-40" : "hover:border-[#111111]/40 hover:shadow-sm",
      "border-[#EEF2F5]",
    ].join(" ")}
  >
    {canEdit && (
      <div className="text-[#9CA3AF] cursor-grab active:cursor-grabbing shrink-0">
        <GripVertical size={16} strokeWidth={1.75} />
      </div>
    )}
    <div className="h-8 w-8 rounded-xl bg-[#F7F8FA] text-[#111111] flex items-center justify-center text-[11px] font-semibold shrink-0">
      {index + 1}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[14px] font-semibold text-[#111111] tracking-tight truncate">
        {step.title || step.label}
      </p>
      <p className="mt-0.5 text-[11.5px] text-[#6B7280] truncate">
        {step.description || "Aucune description"}
      </p>
    </div>
    <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
      {step.visible_client === false && (
        <span className="inline-flex items-center gap-1 rounded-full bg-[#111111] text-white px-2 py-0.5 text-[10px] font-semibold">
          <EyeOff size={9} strokeWidth={2.5} /> Interne
        </span>
      )}
      {step.mandatory !== false && (
        <span className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] text-[#6B7280] px-2 py-0.5 text-[10px] font-semibold">
          Obligatoire
        </span>
      )}
      {step.blocking && (
        <span className="inline-flex items-center gap-1 rounded-full bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 text-[10px] font-semibold">
          <Shield size={9} strokeWidth={2.5} /> Bloque livraison
        </span>
      )}
      {step?.automation?.send_email && (
        <span title="Envoie un email" className="text-[#6B7280]"><Mail size={12} strokeWidth={2} /></span>
      )}
      {step?.automation?.send_sms && (
        <span title="SMS (bientôt)" className="text-[#6B7280]"><MessageSquare size={12} strokeWidth={2} /></span>
      )}
      {step?.automation?.create_planning_event && (
        <span title="Planning" className="text-[#6B7280]"><Calendar size={12} strokeWidth={2} /></span>
      )}
    </div>
  </div>
);

// ------- Step Editor Sheet ---------------------------------------------
const StepSheet = ({ step, onClose, onSave, onDelete, canDelete }) => {
  const [draft, setDraft] = useState(step);
  useEffect(() => setDraft(step), [step]);
  if (!step || !draft) return null;
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setAuto = (patch) => setDraft((d) => ({ ...d, automation: { ...(d.automation || {}), ...patch } }));

  return (
    <div className="fixed inset-0 z-50" data-testid="step-editor-sheet">
      <div onClick={onClose} className="absolute inset-0 bg-[#111111]/25 backdrop-blur-[2px]" />
      <aside
        className="absolute top-0 right-0 h-full w-full sm:w-[520px] bg-white flex flex-col animate-[slide-in-right_.3s_cubic-bezier(0.22,1,0.36,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[#EEF2F5] flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-[10.5px] font-semibold text-[#22C55E] uppercase tracking-wider">Étape</p>
            <input
              data-testid="step-title-input"
              value={draft.title || ""}
              onChange={(e) => set({ title: e.target.value, label: e.target.value })}
              placeholder="Titre de l'étape"
              className="mt-1 w-full text-[22px] font-semibold text-[#111111] tracking-tight outline-none"
            />
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center">
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <Section title="Basique">
            <div className="py-2">
              <label className="block">
                <span className="text-[11.5px] font-medium text-[#6B7280]">Description agence</span>
                <textarea
                  data-testid="step-description-input"
                  value={draft.description || ""}
                  onChange={(e) => set({ description: e.target.value })}
                  placeholder="Ce que l'équipe doit faire à cette étape."
                  rows={2}
                  className="mt-1.5 w-full rounded-xl border border-[#EEF2F5] bg-white px-3 py-2 text-[13px] outline-none focus:border-[#111111] resize-none"
                />
              </label>
            </div>
            <div className="py-2">
              <label className="block">
                <span className="text-[11.5px] font-medium text-[#6B7280]">Message client</span>
                <textarea
                  data-testid="step-client-message-input"
                  value={draft.client_message || ""}
                  onChange={(e) => set({ client_message: e.target.value })}
                  placeholder="Ce que le client verra quand cette étape est en cours."
                  rows={2}
                  className="mt-1.5 w-full rounded-xl border border-[#EEF2F5] bg-white px-3 py-2 text-[13px] outline-none focus:border-[#111111] resize-none"
                />
              </label>
            </div>
          </Section>

          <Section title="Visibilité">
            <Row label="Visible pour l'agence" testId="step-row-visible-agency">
              <Toggle checked={draft.visible_agency !== false}
                      onChange={(v) => set({ visible_agency: v })}
                      testId="step-toggle-visible-agency" />
            </Row>
            <Row label="Visible pour le client"
                 hint="Affichera cette étape dans l'espace client."
                 testId="step-row-visible-client">
              <Toggle checked={draft.visible_client !== false}
                      onChange={(v) => set({ visible_client: v })}
                      testId="step-toggle-visible-client" />
            </Row>
          </Section>

          <Section title="Règles">
            <Row label="Étape obligatoire" testId="step-row-mandatory">
              <Toggle checked={draft.mandatory !== false}
                      onChange={(v) => set({ mandatory: v })}
                      testId="step-toggle-mandatory" />
            </Row>
            <Row label="Bloque la livraison"
                 hint="La livraison ne pourra être marquée terminée tant que cette étape n'est pas complète."
                 testId="step-row-blocking">
              <Toggle checked={!!draft.blocking}
                      onChange={(v) => set({ blocking: v })}
                      testId="step-toggle-blocking" />
            </Row>
            <Row label="Rôle en charge" testId="step-row-role">
              <select
                data-testid="step-role-select"
                value={draft.assigned_role || "agency"}
                onChange={(e) => set({ assigned_role: e.target.value })}
                className="h-9 rounded-xl border border-[#EEF2F5] bg-white px-3 text-[13px]"
              >
                {ROLE_OPTS.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </Row>
          </Section>

          <Section title="Documents">
            <Row label="Demander un document au client"
                 hint="Un badge « Document attendu » apparaît sur l'étape."
                 testId="step-row-request-doc">
              <Toggle checked={!!draft?.automation?.request_document}
                      onChange={(v) => setAuto({ request_document: v })}
                      testId="step-toggle-request-doc" />
            </Row>
            <Row label="Notifier le client quand un document est ajouté"
                 testId="step-row-notify-doc">
              <Toggle checked={!!draft?.automation?.generate_notification}
                      onChange={(v) => setAuto({ generate_notification: v })}
                      testId="step-toggle-notify-doc" />
            </Row>
          </Section>

          <Section title="Automatisations">
            <Row label="Envoyer un email au client"
                 hint="Utilise le template d'étape par défaut."
                 testId="step-row-email">
              <Toggle checked={!!draft?.automation?.send_email}
                      onChange={(v) => setAuto({ send_email: v })}
                      testId="step-toggle-email" />
            </Row>
            <Row label="SMS (bientôt)"
                 hint="Se branchera à Twilio dès l'activation du canal."
                 testId="step-row-sms">
              <Toggle checked={!!draft?.automation?.send_sms}
                      onChange={(v) => setAuto({ send_sms: v })}
                      testId="step-toggle-sms" />
            </Row>
            <Row label="Notification push (bientôt)" testId="step-row-push">
              <Toggle checked={!!draft?.automation?.send_push}
                      onChange={(v) => setAuto({ send_push: v })}
                      testId="step-toggle-push" />
            </Row>
            <Row label="Créer un événement dans le planning"
                 hint="Ex : livraison programmée."
                 testId="step-row-planning">
              <Toggle checked={!!draft?.automation?.create_planning_event}
                      onChange={(v) => setAuto({ create_planning_event: v })}
                      testId="step-toggle-planning" />
            </Row>
            <Row label="Journal (traçabilité)" testId="step-row-log">
              <Toggle checked={draft?.automation?.generate_activity_log !== false}
                      onChange={(v) => setAuto({ generate_activity_log: v })}
                      testId="step-toggle-log" />
            </Row>
          </Section>

          {canDelete && (
            <div className="mt-8 pt-6 border-t border-[#EEF2F5]">
              <button
                data-testid="step-delete"
                onClick={() => {
                  if (confirm(`Supprimer l'étape « ${draft.title || "sans nom"} » ?`)) onDelete(draft);
                }}
                className="inline-flex items-center gap-2 text-[13px] font-medium text-[#991B1B] hover:text-[#7F1D1D]"
              >
                <Trash2 size={13} strokeWidth={2} /> Supprimer cette étape
              </button>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-[#EEF2F5] grid grid-cols-2 gap-2">
          <button
            data-testid="step-cancel"
            onClick={onClose}
            className="h-11 rounded-full bg-white border border-[#EEF2F5] text-[13px] font-semibold text-[#111111]"
          >
            Annuler
          </button>
          <button
            data-testid="step-save"
            onClick={() => onSave(draft)}
            className="h-11 rounded-full bg-[#111111] text-white text-[13px] font-semibold"
          >
            Enregistrer
          </button>
        </div>
      </aside>
    </div>
  );
};

// ------- Preview Panel ---------------------------------------------
const PreviewPanel = ({ open, tpl, onClose }) => {
  const [tab, setTab] = useState("agency");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !tpl?.id) return;
    setLoading(true);
    api.previewWorkflowTemplate(tpl.id)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, tpl?.id]);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="workflow-preview">
      <div onClick={onClose} className="absolute inset-0 bg-[#111111]/40 backdrop-blur-[2px]" />
      <div className="absolute inset-y-0 right-0 w-full lg:w-[720px] bg-white flex flex-col animate-[slide-in-right_.3s_cubic-bezier(0.22,1,0.36,1)]">
        <div className="p-6 border-b border-[#EEF2F5] flex items-start justify-between">
          <div>
            <p className="text-[10.5px] font-semibold text-[#22C55E] uppercase tracking-wider">Aperçu</p>
            <h3 className="mt-1 text-[20px] font-semibold text-[#111111] tracking-tight">
              {tpl.name}
            </h3>
            <p className="mt-1 text-[12.5px] text-[#6B7280]">
              Vérifie comment ce workflow apparaîtra côté agence et côté client.
            </p>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center">
            <X size={16} strokeWidth={2} />
          </button>
        </div>
        <div className="px-6 py-3 border-b border-[#EEF2F5] flex items-center gap-1.5">
          <button
            data-testid="preview-tab-agency"
            onClick={() => setTab("agency")}
            className={`h-9 rounded-full px-4 text-[12.5px] font-semibold transition-colors ${
              tab === "agency" ? "bg-[#111111] text-white" : "bg-white text-[#6B7280] hover:bg-[#F7F8FA]"
            }`}
          >
            Vue agence
          </button>
          <button
            data-testid="preview-tab-client"
            onClick={() => setTab("client")}
            className={`h-9 rounded-full px-4 text-[12.5px] font-semibold transition-colors ${
              tab === "client" ? "bg-[#111111] text-white" : "bg-white text-[#6B7280] hover:bg-[#F7F8FA]"
            }`}
          >
            Vue client
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 bg-[#F7F8FA]">
          {loading || !data ? (
            <div className="text-[13px] text-[#6B7280]">Génération de l&apos;aperçu…</div>
          ) : tab === "agency" ? (
            <>
              {data.delivery_gate?.blocked && (
                <div className="rounded-2xl border border-[#FECACA] bg-white p-4 mb-4" data-testid="preview-delivery-gate">
                  <p className="text-[10.5px] font-semibold text-[#991B1B] uppercase tracking-wider">
                    Livraison bloquée uniquement lorsque
                  </p>
                  <p className="mt-1 text-[13.5px] text-[#111111] leading-relaxed">
                    {data.delivery_gate.blocked_by.length} étape{data.delivery_gate.blocked_by.length > 1 ? "s" : ""} obligatoire{data.delivery_gate.blocked_by.length > 1 ? "s" : ""} sont terminée{data.delivery_gate.blocked_by.length > 1 ? "s" : ""} :
                    {" "}<span className="font-semibold">{data.delivery_gate.blocked_by.map((b) => b.label).join(", ")}</span>.
                  </p>
                </div>
              )}
              <WorkflowTimeline
                steps={data.agency_view}
                workflowSummary={{
                  totals: {
                    total: data.agency_view.length,
                    completed: 0, blocked: 0, waiting: 0,
                  },
                  active_step: data.agency_view[0]
                    ? { label: data.agency_view[0].title, key: data.agency_view[0].key }
                    : null,
                  next_action: null,
                }}
                onSelect={() => {}}
              />
            </>
          ) : (
            <div className="bg-white rounded-2xl overflow-hidden max-w-[420px] mx-auto p-4">
              {data.client_view.length === 0 ? (
                <div className="text-center py-8 text-[13px] text-[#6B7280]">
                  Aucune étape n&apos;est visible pour le client dans ce workflow.
                </div>
              ) : (
                <ClientTimeline steps={data.client_view} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ------- Main Editor Page ------------------------------------------
const WorkflowEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const isAdmin = ["admin", "agency_owner", "platform_admin"].includes(user?.role);

  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.getWorkflowTemplate(id)
      .then((t) => setTpl(t))
      .catch((e) => setError(e.message || "Erreur"))
      .finally(() => setLoading(false));
  }, [id]);

  const canEdit = isAdmin && tpl && tpl.status !== "archived";

  const set = (patch) => setTpl((t) => ({ ...t, ...patch }));
  const setSteps = (fn) => setTpl((t) => ({ ...t, steps: fn(t.steps || []) }));

  const openStep = (s) => setActiveStep(s);
  const saveStep = (draft) => {
    setSteps((steps) => steps.map((s) => (s.id === draft.id ? { ...s, ...draft, label: draft.title } : s)));
    setActiveStep(null);
  };
  const deleteStep = (draft) => {
    setSteps((steps) => steps.filter((s) => s.id !== draft.id));
    setActiveStep(null);
  };
  const addStep = () => {
    const newStep = {
      id: uid(),
      key: `step_${uid()}`,
      title: "Nouvelle étape",
      label: "Nouvelle étape",
      description: "",
      icon: "circle",
      color: "gray",
      order: (tpl?.steps?.length || 0) + 1,
      visible_agency: true,
      visible_client: true,
      mandatory: true,
      blocking: false,
      assigned_role: "agency",
      state: "not_started",
      status: "a_venir",
      blocks_until: [],
      automation: {
        send_email: false, send_sms: false, send_push: false,
        create_planning_event: false, create_task: false,
        request_document: false, generate_notification: false,
        generate_activity_log: true, unlock_next_step: true,
      },
      client_message: "",
    };
    setSteps((steps) => [...steps, newStep]);
    setActiveStep(newStep);
  };

  const onDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    setSteps((steps) => {
      const copy = [...steps];
      const [moved] = copy.splice(dragIdx, 1);
      copy.splice(idx, 0, moved);
      return copy.map((s, i) => ({ ...s, order: i + 1 }));
    });
    setDragIdx(null);
  };

  const blockingSteps = useMemo(
    () => (tpl?.steps || []).filter((s) => s.blocking && s.mandatory !== false),
    [tpl?.steps],
  );

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    try {
      const patch = {
        name: tpl.name,
        description: tpl.description,
        steps: tpl.steps,
        payment_config: tpl.payment_config || null,
      };
      const updated = await api.updateWorkflowTemplate(id, patch);
      setTpl(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setError(e?.data?.detail || e.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-[13px] text-[#6B7280]">Chargement…</div>;
  if (!tpl) return (
    <div className="p-8">
      <p className="text-[13px] text-[#991B1B]">{error || "Workflow introuvable"}</p>
      <button onClick={() => navigate("/app/parametres/workflows")} className="mt-4 h-9 rounded-full bg-[#111111] text-white text-[12.5px] px-4">
        Retour à la liste
      </button>
    </div>
  );

  const readOnlyBanner = !canEdit ? (
    <div className="mt-4 rounded-2xl bg-[#FEF9E4] border border-[#F5E6BD] px-4 py-3 text-[13px] text-[#92400E]" data-testid="editor-readonly">
      <div className="flex items-start gap-2">
        <Info size={14} strokeWidth={2} className="mt-0.5 shrink-0" />
        <p>
          {tpl.status === "archived"
            ? "Ce workflow est archivé. Réactive-le depuis la liste pour l'éditer à nouveau."
            : "Tu n'as pas les droits pour modifier ce workflow."}
        </p>
      </div>
    </div>
  ) : null;

  return (
    <div className="wyvio-fade-in" data-testid="page-workflow-editor">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div className="min-w-0">
          <button
            onClick={() => navigate("/app/parametres/workflows")}
            data-testid="editor-back"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#6B7280] hover:text-[#111111]"
          >
            <ArrowLeft size={13} strokeWidth={2} /> Workflows
          </button>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <input
              data-testid="editor-name-input"
              value={tpl.name || ""}
              disabled={!canEdit}
              onChange={(e) => set({ name: e.target.value })}
              className="text-[28px] leading-tight font-semibold tracking-tight text-[#111111] outline-none bg-transparent disabled:cursor-not-allowed"
            />
            {tpl.is_default && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#C8FF2F] text-[#111111] px-2 py-0.5 text-[10px] font-semibold">
                <Star size={10} strokeWidth={2.5} /> Défaut
              </span>
            )}
            <span className="text-[11.5px] text-[#9CA3AF]">v{tpl.version || 1}</span>
          </div>
          <input
            data-testid="editor-description-input"
            value={tpl.description || ""}
            disabled={!canEdit}
            placeholder="Courte description de ce workflow…"
            onChange={(e) => set({ description: e.target.value })}
            className="mt-2 w-full max-w-xl text-[14px] text-[#6B7280] outline-none bg-transparent placeholder:text-[#9CA3AF]"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap self-start lg:self-auto">
          <button
            data-testid="editor-preview-btn"
            onClick={() => setPreviewOpen(true)}
            className="h-11 rounded-full bg-white border border-[#EEF2F5] text-[13px] font-semibold text-[#111111] px-4 inline-flex items-center gap-1.5 hover:bg-[#F7F8FA]"
          >
            <Play size={13} strokeWidth={2} />
            Aperçu
          </button>
          {canEdit && (
            <button
              data-testid="editor-save-btn"
              onClick={handleSave}
              disabled={saving}
              className="h-11 rounded-full bg-[#111111] text-white text-[13px] font-semibold px-5 inline-flex items-center gap-2 hover:bg-black disabled:opacity-50 transition-colors"
            >
              {saved ? <><Check size={14} strokeWidth={2.5} /> Publié</>
                     : saving ? "Enregistrement…" : <><Save size={14} strokeWidth={2} /> Publier</>}
            </button>
          )}
        </div>
      </header>

      {readOnlyBanner}

      {error && (
        <div className="mt-4 rounded-2xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-[13px] text-[#991B1B]">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}

      {/* Summary of blocking rules */}
      <section className="mt-6 rounded-2xl bg-[#F7F8FA] border border-[#EEF2F5] p-4" data-testid="editor-delivery-summary">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-2xl bg-[#111111] text-white flex items-center justify-center shrink-0">
            <Shield size={15} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#111111]">Livraison autorisée</p>
            <p className="mt-0.5 text-[12.5px] text-[#6B7280] leading-relaxed">
              {blockingSteps.length === 0
                ? "Aucune étape ne bloque la livraison — elle peut être marquée terminée à tout moment."
                : `Uniquement lorsque ${blockingSteps.length} étape${blockingSteps.length > 1 ? "s" : ""} obligatoire${blockingSteps.length > 1 ? "s" : ""} sont terminée${blockingSteps.length > 1 ? "s" : ""} : ${blockingSteps.map((s) => s.title || s.label).join(", ")}.`}
            </p>
          </div>
        </div>
      </section>

      {/* Steps list */}
      <section className="mt-6" data-testid="editor-steps">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[13px] font-semibold text-[#111111]">
            Étapes ({tpl.steps?.length || 0})
          </p>
          {canEdit && (
            <button
              data-testid="add-step-btn"
              onClick={addStep}
              className="h-9 rounded-full bg-[#C8FF2F] text-[#111111] text-[12.5px] font-semibold px-3.5 inline-flex items-center gap-1.5 hover:bg-[#b8ef1d]"
            >
              <Plus size={13} strokeWidth={2.25} /> Ajouter une étape
            </button>
          )}
        </div>
        <div className="space-y-2">
          {(tpl.steps || []).map((step, idx) => (
            <StepCard
              key={step.id || step.key}
              step={step}
              index={idx}
              canEdit={canEdit}
              dragging={dragIdx === idx}
              onOpen={openStep}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
            />
          ))}
          {(!tpl.steps || tpl.steps.length === 0) && (
            <div className="rounded-2xl border border-dashed border-[#EEF2F5] p-6 text-center text-[13px] text-[#6B7280]">
              Aucune étape encore. Ajoute la première étape.
            </div>
          )}
        </div>
      </section>

      <p className="mt-6 text-[11.5px] text-[#9CA3AF]">
        Les modifications ne s&apos;appliquent qu&apos;aux futurs dossiers. Les dossiers existants gardent leur workflow tel qu&apos;au moment de leur création.
      </p>

      <StepSheet
        step={activeStep}
        canDelete={canEdit && (tpl.steps?.length || 0) > 1}
        onClose={() => setActiveStep(null)}
        onSave={saveStep}
        onDelete={deleteStep}
      />

      <PreviewPanel
        open={previewOpen}
        tpl={tpl}
        onClose={() => setPreviewOpen(false)}
      />
    </div>
  );
};

export default WorkflowEditor;
